import { useEffect, useState } from "react";

import DeleteIcon from "@mui/icons-material/Delete";
import Box from "@mui/material/Box";
import FormControl from "@mui/material/FormControl";
import IconButton from "@mui/material/IconButton";
import InputLabel from "@mui/material/InputLabel";
import MenuItem from "@mui/material/MenuItem";
import Select from "@mui/material/Select";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import Tooltip from "@mui/material/Tooltip";

import { ssoGroupsMappingText } from "../../../../assets/texts/Settings/sso";
import { DraggableItemHolder } from "../../../../Components/DragAndDrop/DraggableItemHolder";
import { type GroupMapping, type IdNameMapping } from "./SsoGroupMappingList";

const GROUP_ID_REGEX = /^[a-zA-Z0-9_-]+$/;

type GroupMappingItemProps = GroupMapping & {
  id: string;
  rolesList: IdNameMapping[];
  orgsList: IdNameMapping[];
  grabbing?: boolean;
  onDelete: () => void;
  onChange: (id: string, field: keyof GroupMapping, value: string) => void;
  showOrganizationColumn: boolean;
  disabled?: boolean;
  existingGroupIds?: string[];
  onValidationChange?: (id: string, hasError: boolean) => void;
};

export const cyIds = {
  box: "draggable-item-box", // This one doesn't relate to the ID in any way because there are times we want to get ALL the rows rather than a particular one.
  listItem: (id: string) => `sso-group-mapping-item-${id}`,
  orderHandle: (id: string) => `order-handle-${id}`,
  fields: {
    groupId: (id: string) => `group-id-field-${id}`,
    role: (id: string) => `role-select-${id}`,
    organization: (id: string) => `organization-select-${id}`,
  },
  buttons: {
    delete: (id: string) => `delete-mapping-${id}`,
  },
};

const SsoGroupMappingItem = ({
  id,
  groupId,
  role,
  rolesList,
  organization,
  orgsList,
  grabbing = false,
  onDelete,
  onChange,
  showOrganizationColumn,
  disabled = false,
  existingGroupIds = [],
  onValidationChange,
}: GroupMappingItemProps) => {
  const isDefaultRow = groupId === ssoGroupsMappingText.GROUP_ID_DEFAULT;

  const [groupIdError, setGroupIdError] = useState<boolean>(false);
  const [groupIdHelperText, setGroupIdHelperText] = useState<string>("");
  const [hasStartedTyping, setHasStartedTyping] = useState<boolean>(false);

  /**
   * Validation logic - calculates and sets error state
   */
  useEffect(() => {
    const isGroupIdEmpty: boolean = groupId === "";
    const isGroupIdValid: boolean = groupId === "" || GROUP_ID_REGEX.test(groupId);
    const isGroupIdPermitted: boolean = groupId === "" || !existingGroupIds.includes(groupId);

    // Show error if user has started typing and field is empty, or if format/uniqueness validation fails
    const hasError =
      !disabled && !isDefaultRow && ((hasStartedTyping && isGroupIdEmpty) || !isGroupIdValid || !isGroupIdPermitted);
    setGroupIdError(hasError);

    if (!hasError) {
      setGroupIdHelperText("");
      return;
    }

    let helperText = "";
    if (hasStartedTyping && isGroupIdEmpty) {
      helperText = "Group ID is required";
    } else if (!isGroupIdValid) {
      helperText = ssoGroupsMappingText.GROUP_ID_FORMAT_ERROR;
    } else if (!isGroupIdPermitted) {
      helperText = ssoGroupsMappingText.GROUP_ID_UNIQUE_ERROR;
    }
    setGroupIdHelperText(helperText);
  }, [disabled, isDefaultRow, groupId, existingGroupIds, hasStartedTyping]);

  /**
   * Notify parent when error state changes
   * Kept separate for optimisation
   */
  useEffect(() => {
    onValidationChange?.(id, groupIdError);
  }, [id, groupIdError, onValidationChange]);

  return (
    <Box sx={{ py: 0.75 }} data-cy={cyIds.box}>
      <DraggableItemHolder id={id} grabbing={grabbing} height={"fit-content"} disabled={disabled}>
        <Stack
          direction="row"
          gap={1}
          alignItems="flex-start"
          sx={{ width: "100%", paddingRight: 1, py: 2 }}
          data-cy={cyIds.listItem(id)}
        >
          <TextField
            size="small"
            value={groupId}
            placeholder={ssoGroupsMappingText.GROUP_ID_DEFAULT}
            label={ssoGroupsMappingText.GROUP_ID_LABEL}
            sx={{ flex: 1 }}
            data-cy={cyIds.orderHandle(id)}
            slotProps={{ htmlInput: { "data-cy": cyIds.fields.groupId(id) } }}
            onChange={(e) => {
              setHasStartedTyping(true);
              onChange(id, "groupId", e.target.value);
            }}
            disabled={disabled}
            error={groupIdError}
            helperText={groupIdHelperText}
          />
          <FormControl size="small" sx={{ flex: 1 }}>
            <InputLabel id={`role-label-${id}`}>{ssoGroupsMappingText.ROLE_ID_LABEL}</InputLabel>
            <Select
              labelId={`role-label-${id}`}
              value={role}
              label={ssoGroupsMappingText.ROLE_ID_LABEL}
              data-cy={cyIds.fields.role(id)}
              onChange={(e) => {
                onChange(id, "role", e.target.value);
              }}
              disabled={disabled}
            >
              {role === "default" ? (
                <MenuItem value="default">{ssoGroupsMappingText.ROLE_VALUE_DEFAULT}</MenuItem>
              ) : (
                rolesList.map((option) => (
                  <MenuItem key={option.id} value={option.id}>
                    {option.name}
                  </MenuItem>
                ))
              )}
            </Select>
          </FormControl>
          {showOrganizationColumn && (
            <FormControl size="small" sx={{ flex: 1 }}>
              <InputLabel id={`organization-label-${id}`}>{ssoGroupsMappingText.ORGANIZATION_ID_LABEL}</InputLabel>
              <Tooltip
                placement="bottom-start"
                arrow={true}
                title={
                  role === ssoGroupsMappingText.ROLE_ID_DEFAULT ? ssoGroupsMappingText.ORGANIZATION_ID_TOOLTIP : ""
                }
              >
                <Select
                  labelId={`organization-label-${id}`}
                  value={organization}
                  label={ssoGroupsMappingText.ORGANIZATION_ID_LABEL}
                  data-cy={cyIds.fields.organization(id)}
                  onChange={(e) => {
                    onChange(id, "organization", e.target.value);
                  }}
                  disabled={disabled}
                >
                  {orgsList.map((option) => (
                    <MenuItem key={option.id} value={option.id}>
                      {option.name}
                    </MenuItem>
                  ))}
                </Select>
              </Tooltip>
            </FormControl>
          )}
          <IconButton
            size="small"
            onClick={onDelete}
            data-cy={cyIds.buttons.delete(id)}
            disabled={disabled}
            sx={{ mt: 0.25 }}
          >
            <DeleteIcon />
          </IconButton>
        </Stack>
      </DraggableItemHolder>
    </Box>
  );
};

export default SsoGroupMappingItem;
